import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import Grid from "@mui/material/Grid";
import Chip from "@mui/material/Chip";
import Stack from "@mui/material/Stack";
import CloseIcon from "@mui/icons-material/Close";
import RecibosImputadosFactura from "./RecibosImputadosFactura.js";
import RemitosAsociadosFactura from "./RemitosAsociadosFactura.js";
import {
  formatCurrency,
  formatDate,
  formatComprobanteNumber,
  formatCuit,
  getEstado,
  getEstadoConfig,
} from "../utils/formatters.js";

function Field({ label, value, mono = false }) {
  return (
    <Box>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mb: 0.25 }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          fontWeight: 500,
          fontFamily: mono ? '"JetBrains Mono", monospace' : "inherit",
        }}
      >
        {value ?? "-"}
      </Typography>
    </Box>
  );
}

function SectionTitle({ children }) {
  return (
    <Typography
      variant="overline"
      color="text.secondary"
      sx={{ fontWeight: 700, letterSpacing: "0.08em" }}
    >
      {children}
    </Typography>
  );
}

function ComprobanteDetailDialog({ open, onClose, comprobante }) {
  if (!comprobante) return null;

  const estado = getEstado(comprobante);
  const estadoConfig = getEstadoConfig(estado);
  const numero = formatComprobanteNumber(
    comprobante.pto_venta,
    comprobante.nro_comprobante,
  );
  const tipo = `${comprobante.texto_comprobante} ${comprobante.letra ? `"${comprobante.letra}"` : ""}`;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle sx={{ pr: 6 }}>
        <Stack
          direction="row"
          alignItems="center"
          spacing={1.5}
          flexWrap="wrap"
        >
          <Typography variant="h6" component="span">
            {tipo}
          </Typography>
          <Chip
            size="small"
            label={estadoConfig.label}
            color={estadoConfig.color}
          />
        </Stack>
        <Typography variant="body2" color="text.secondary">
          N° {numero}
        </Typography>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            position: "absolute",
            right: 12,
            top: 12,
            color: "text.secondary",
          }}
          aria-label="Cerrar"
        >
          <CloseIcon fontSize="small" />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>
        <Stack spacing={3}>
          <Box>
            <SectionTitle>Comprobante</SectionTitle>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={6} sm={6}>
                <Field label="Comprobante" value={tipo} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <Field label="Número" value={numero} mono />
              </Grid>
              <Grid item xs={6} sm={3}>
                <Field
                  label="Fecha de emisión"
                  value={formatDate(comprobante.fecha_emision)}
                  mono
                />
              </Grid>
              <Grid item xs={6} sm={12}>
                <Field
                  label="Cond. de pago"
                  value={comprobante.condicion_pago}
                />
              </Grid>
            </Grid>
          </Box>
          <Divider />
          <Box>
            <SectionTitle>Cliente</SectionTitle>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={6}>
                <Field label="Razón social" value={comprobante.razon_social} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <Field
                  label="CUIT / DNI"
                  value={formatCuit(comprobante.cuit_dni)}
                  mono
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <Field
                  label="Condición IVA"
                  value={comprobante.condicion_iva}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <Field label="Domicilio" value={comprobante.domicilio} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <Field label="Localidad" value={comprobante.localidad} />
              </Grid>
              <Grid item xs={6} sm={3}>
                <Field label="Provincia" value={comprobante.provincia} />
              </Grid>
            </Grid>
          </Box>

          <Divider />

          <Box>
            <SectionTitle>Importes</SectionTitle>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={6} sm={3}>
                <Field
                  label="Neto"
                  value={formatCurrency(comprobante.importe_neto)}
                  mono
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <Field
                  label="IVA"
                  value={formatCurrency(comprobante.importe_iva)}
                  mono
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <Field
                  label="Tributos"
                  value={formatCurrency(comprobante.importe_tributos)}
                  mono
                />
              </Grid>
              <Grid item xs={12}>
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    p: 2,
                    borderRadius: 2,
                    bgcolor: "action.hover",
                  }}
                >
                  <Typography variant="subtitle1">Total</Typography>
                  <Typography
                    variant="h6"
                    sx={{ fontFamily: '"JetBrains Mono", monospace' }}
                  >
                    {formatCurrency(comprobante.importe_total)}
                  </Typography>
                </Box>
              </Grid>
            </Grid>
          </Box>

          <Divider />

          <Box>
            <SectionTitle>Pago</SectionTitle>
            <RecibosImputadosFactura idFactura={comprobante.id_factura} />
          </Box>

          <Divider />

          <Box>
            <SectionTitle>Remitos asociados</SectionTitle>
            <RemitosAsociadosFactura idFactura={comprobante.id_factura} />
          </Box>

          <Divider />

          <Box>
            <SectionTitle>AFIP / ARCA</SectionTitle>
            <Grid container spacing={2} sx={{ mt: 0.5 }}>
              <Grid item xs={12} sm={4}>
                <Field label="CAE" value={comprobante.cae} mono />
              </Grid>
              <Grid item xs={6} sm={4}>
                <Field label="Estado" value={estadoConfig.label} />
              </Grid>
            </Grid>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2 }}>
        <Button onClick={onClose} variant="contained">
          Cerrar
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ComprobanteDetailDialog;
